<template>
  <div class="dashboard-peak-load">
    <div class="peak-header">
      <div>
        <h3 class="peak-title">{{ $t('org-dashboard.peak-load') }}</h3>
        <p class="peak-subtitle">{{ $t('org-dashboard.peak-load-subtitle') }}</p>
      </div>
      <div class="peak-toggle">
        <v-btn-toggle :value="activeViewMode" @change="$emit('update:activeViewMode', $event)" mandatory dense class="peak-btn-toggle">
          <v-btn x-small :value="mode.value" v-for="mode in viewModes" :key="mode.value">{{ mode.label }}</v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="peak-loading">
      <v-skeleton-loader v-for="n in 3" :key="n" type="card" class="peak-skeleton" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!slots || slots.length === 0" class="peak-empty">
      <v-icon color="grey lighten-1" size="40">mdi-chart-timeline-variant</v-icon>
      <p>{{ $t('org-dashboard.peak-no-data') }}</p>
    </div>

    <!-- Slot cards -->
    <div v-else class="peak-grid">
      <div
        v-for="(slot, index) in slots"
        :key="index"
        class="peak-slot-card"
      >
        <div class="peak-slot-rank">#{{ index + 1 }}</div>
        <div class="peak-slot-body">
          <div class="peak-slot-time">
            {{ getDayLabel(slot.day) }} {{ padHour(slot.hourStart) }}h-{{ padHour(slot.hourEnd) }}h
          </div>
          <div class="peak-slot-value">{{ formatSlotValue(slot.value) }}</div>
          <div class="peak-slot-sessions" v-if="slot.sessions != null && activeViewMode !== 'sessions' && activeViewMode !== 'kwh'">
            {{ slot.sessions }} {{ $t('org-dashboard.sessions-short').toLowerCase() }}
          </div>
        </div>
        <div v-if="slot.chargers && slot.chargers.length" class="peak-slot-chargers">
          <div class="peak-slot-chargers-label">{{ $t('org-dashboard.peak-chargers') }}</div>
          <div
            v-for="(charger, ci) in slot.chargers.slice(0, 3)"
            :key="ci"
            class="peak-slot-charger"
            @click="$emit('charger-click', { chargingPointId: charger.chargingPointId })"
          >
            <v-icon x-small color="#6b7280" class="mr-1">mdi-ev-plug-type2</v-icon>
            <span class="peak-charger-name">{{ charger.name }}</span>
            <span class="peak-charger-value">{{ charger.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardPeakLoad',
  props: {
    slots: { type: Array, default: () => [] },
    viewModes: { type: Array, default: () => [] },
    activeViewMode: { type: String, default: 'sessions' },
    loading: { type: Boolean, default: false },
  },

  methods: {
    getDayLabel(day) {
      const dayKeys = ['day-mon', 'day-tue', 'day-wed', 'day-thu', 'day-fri', 'day-sat', 'day-sun']
      if (day >= 1 && day <= 7) {
        return this.$t('org-dashboard.' + dayKeys[day - 1])
      }
      return String(day)
    },

    padHour(h) {
      return String(h).padStart(2, '0')
    },

    formatSlotValue(value) {
      if (value == null) return '-'
      if (this.activeViewMode === 'kwh') return value.toFixed(1) + ' kWh'
      if (this.activeViewMode === 'usage') return Math.round(value) + '%'
      return Math.round(value).toLocaleString()
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-peak-load {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.peak-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.peak-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.peak-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.peak-btn-toggle .v-btn {
  text-transform: none !important;
  font-size: 10px !important;
  letter-spacing: 0 !important;
  min-width: 40px !important;
}

.peak-loading {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.peak-skeleton {
  border-radius: 8px;
}

.peak-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;

  p {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
  }
}

.peak-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.peak-slot-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 14px;
  border: 1px solid #f3f4f6;
  position: relative;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.peak-slot-rank {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #FF5722;
  background: #FFF3E0;
  padding: 2px 8px;
  border-radius: 10px;
}

.peak-slot-time {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.peak-slot-value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.peak-slot-sessions {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.peak-slot-chargers {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
}

.peak-slot-chargers-label {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.peak-slot-charger {
  display: flex;
  align-items: center;
  padding: 3px 0;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }
}

.peak-charger-name {
  font-size: 11px;
  color: #1976d2;
  font-weight: 500;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.peak-charger-value {
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  margin-left: 8px;
  flex-shrink: 0;
}
</style>
